(function (global) {
  'use strict';

  const SIZE_ORDER = ['22.5 cm / EU 36–37', '23.5 cm / EU 38–39', '24.5 cm / EU 40–41', '26.0 cm / EU 42–43', '27.0 cm / EU 44–45'];
  const PAIR_COUNTS = [1, 2, 3];
  const TIER_TOTALS = { 1: '$32.99', 2: '$59.99', 3: '$84.99' };
  const PROMO_END_AT = 0;

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function normalizeCandidates(rawCandidates) {
    const seen = new Set();
    return (Array.isArray(rawCandidates) ? rawCandidates : [])
      .filter((candidate) => {
        const id = Number(candidate?.id);
        return Number.isInteger(id)
          && id > 0
          && typeof candidate.color === 'string'
          && candidate.color.trim().length > 0
          && SIZE_ORDER.includes(candidate.size)
          && candidate.available === true
          && typeof candidate.buyId === 'string'
          && candidate.buyId.trim().length > 0;
      })
      .map((candidate) => ({
        id: Number(candidate.id),
        gid: String(candidate.gid || ''),
        color: candidate.color.trim(),
        size: candidate.size,
        displaySize: candidate.displaySize || candidate.size,
        inferredUsWomenSize: candidate.inferredUsWomenSize ?? null,
        available: true,
        buyId: candidate.buyId.trim(),
      }))
      .filter((candidate) => {
        const key = `${candidate.id}|${candidate.color}|${candidate.size}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((left, right) => (
        left.color.localeCompare(right.color, 'en')
        || SIZE_ORDER.indexOf(left.size) - SIZE_ORDER.indexOf(right.size)
        || left.id - right.id
      ));
  }

  function normalizeVariantMedia(rawEntries) {
    const seen = new Set();
    return (Array.isArray(rawEntries) ? rawEntries : [])
      .map((entry) => ({
        variantId: Number(entry?.variantId),
        color: typeof entry?.color === 'string' ? entry.color.trim() : '',
        mediaId: entry?.mediaId == null ? '' : String(entry.mediaId).trim(),
      }))
      .filter((entry) => (
        Number.isInteger(entry.variantId)
        && entry.variantId > 0
        && entry.color.length > 0
        && entry.mediaId.length > 0
      ))
      .filter((entry) => {
        if (seen.has(entry.variantId)) return false;
        seen.add(entry.variantId);
        return true;
      });
  }

  function resolveMediaIdForColor(candidates, variantMedia, color) {
    const selectedColor = String(color || '').trim();
    if (!selectedColor) return '';

    const mediaByVariantId = new Map(
      variantMedia
        .filter((entry) => entry.color === selectedColor)
        .map((entry) => [entry.variantId, entry.mediaId])
    );
    const candidate = candidates.find((entry) => (
      entry.color === selectedColor && mediaByVariantId.has(entry.id)
    ));
    return candidate ? mediaByVariantId.get(candidate.id) : '';
  }

  function activateGalleryMedia(root, mediaId) {
    const normalizedMediaId = String(mediaId || '').trim();
    const productInfo = root?.closest?.('product-info');
    const sectionId = productInfo?.dataset?.section;
    const gallery = productInfo?.querySelector?.('media-gallery');
    if (!normalizedMediaId || !sectionId || !gallery || typeof gallery.setActiveMedia !== 'function') {
      return false;
    }

    const targetId = `${sectionId}-${normalizedMediaId}`;
    const targetExists = Array.from(gallery.querySelectorAll('[data-media-id]'))
      .some((node) => node?.dataset?.mediaId === targetId);
    if (!targetExists) return false;

    gallery.setActiveMedia(targetId, true);
    return true;
  }

  function renderSizeOptions(candidates, color) {
    return candidates
      .filter((candidate) => candidate.color === color)
      .map((candidate) => `<option value="${escapeHtml(candidate.size)}">${escapeHtml(candidate.displaySize)}</option>`)
      .join('');
  }

  function groupVariantIds(ids) {
    const grouped = new Map();
    ids.forEach((value) => {
      const id = Number(value);
      if (!Number.isInteger(id) || id <= 0) return;
      grouped.set(id, (grouped.get(id) || 0) + 1);
    });
    return Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
  }

  function createOfferModel(rawCandidates) {
    const candidates = normalizeCandidates(rawCandidates);
    const selections = Array.from({ length: 3 }, () => ({ color: '', size: '' }));
    const model = {
      candidates,
      pairCount: 1,
      selections,
      setPairCount(value) {
        const count = Number(value);
        if (!PAIR_COUNTS.includes(count)) return false;
        this.pairCount = count;
        return true;
      },
      setSelection(index, key, value) {
        if (!Number.isInteger(index) || index < 0 || index > 2) return false;
        if (key !== 'color' && key !== 'size') return false;
        this.selections[index][key] = String(value || '');
        return true;
      },
      buildItems() {
        const variantIds = [];
        for (let index = 0; index < this.pairCount; index += 1) {
          const selection = this.selections[index];
          if (!selection.color || !selection.size) {
            return {
              ok: false,
              error: `Choose a color and size for pair ${index + 1}.`,
              items: [],
            };
          }
          const match = this.candidates.find((candidate) => (
            candidate.color === selection.color && candidate.size === selection.size
          ));
          if (!match) {
            return {
              ok: false,
              error: `${selection.color} / ${selection.size} is unavailable for pair ${index + 1}. Choose another combination.`,
              items: [],
            };
          }
          variantIds.push(match.id);
        }
        return { ok: true, error: '', items: groupVariantIds(variantIds) };
      },
    };
    return model;
  }

  function createSubmitHandler(options) {
    const model = options.model;
    const post = options.post;
    const setBusy = options.setBusy || (() => {});
    const announce = options.announce || (() => {});
    const onSuccess = options.onSuccess || (() => {});
    let busy = false;

    return async function submit() {
      if (busy) return { ok: false, ignored: true };
      const built = model.buildItems();
      if (!built.ok) {
        announce(built.error);
        return { ok: false, error: built.error };
      }

      busy = true;
      setBusy(true);
      announce('Adding your selection to cart…');
      try {
        const response = await post(built.items);
        if (response?.ok === false) throw new Error(response.error || 'Unable to add your selection to cart.');
        onSuccess(response);
        announce('Added to cart.');
        return { ok: true, response };
      } catch (error) {
        const message = error?.message || 'Unable to add your selection to cart. Try again.';
        announce(message);
        return { ok: false, error: message };
      } finally {
        busy = false;
        setBusy(false);
      }
    };
  }

  function performInitialization(options) {
    try {
      const custom = options.buildCustom();
      if (!custom) return false;
      options.hideNative();
      return custom;
    } catch (error) {
      return false;
    }
  }

  function renderOfferMarkup(rawCandidates, shippingLabel) {
    const candidates = normalizeCandidates(rawCandidates);
    const colors = [...new Set(candidates.map((candidate) => candidate.color))];
    const colorOptions = colors.map((color) => (
      `<option value="${escapeHtml(color)}">${escapeHtml(color)}</option>`
    )).join('');
    const pairRows = PAIR_COUNTS.map((pairNumber) => `
      <fieldset class="cloudsoft-volume-offer__pair" data-cloudsoft-pair="${pairNumber}"${pairNumber > 1 ? ' hidden' : ''}>
        <legend>Pair ${pairNumber}</legend>
        <label>Color
          <select data-cloudsoft-color="${pairNumber}" required>
            <option value="">Choose color</option>${colorOptions}
          </select>
        </label>
        <label>Size
          <select data-cloudsoft-size="${pairNumber}" required disabled>
            <option value="">Choose size</option>
          </select>
        </label>
      </fieldset>`).join('');

    return `
      <form class="cloudsoft-volume-offer__form" data-cloudsoft-form novalidate>
        <div class="cloudsoft-volume-offer__campaign">
          <span>EVERYDAY COMFORT SAVINGS</span>
          <strong>Choose your offer</strong>
          <p>Savings are applied automatically at checkout.</p>
        </div>
        <div class="cloudsoft-volume-offer__tiers" role="radiogroup" aria-label="Choose number of pairs">
          <label class="cloudsoft-volume-offer__tier">
            <input type="radio" name="cloudsoft-pair-count" value="1" checked>
            <span class="cloudsoft-volume-offer__tier-copy"><strong>1 PAIR</strong><small>A simple start for everyday comfort</small></span>
            <span class="cloudsoft-volume-offer__tier-price"><s>$49.99</s><strong>$32.99</strong><small>Save $17.00</small></span>
          </label>
          <label class="cloudsoft-volume-offer__tier">
            <input type="radio" name="cloudsoft-pair-count" value="2">
            <span class="cloudsoft-volume-offer__badge">MOST POPULAR</span>
            <span class="cloudsoft-volume-offer__tier-copy"><strong>2 PAIRS</strong><small>Mix colors and sizes</small></span>
            <span class="cloudsoft-volume-offer__tier-price"><s>$99.98</s><strong>$59.99</strong><small>Save $39.99</small></span>
          </label>
          <label class="cloudsoft-volume-offer__tier">
            <input type="radio" name="cloudsoft-pair-count" value="3">
            <span class="cloudsoft-volume-offer__badge cloudsoft-volume-offer__badge--value">BEST VALUE</span>
            <span class="cloudsoft-volume-offer__tier-copy"><strong>3 PAIRS</strong><small>Best everyday bundle savings</small></span>
            <span class="cloudsoft-volume-offer__tier-price"><s>$149.97</s><strong>$84.99</strong><small>Save $64.98</small></span>
          </label>
        </div>
        <p class="cloudsoft-volume-offer__deadline">Automatic discount · No code needed</p>
        ${pairRows}
        <p class="cloudsoft-volume-offer__shipping">${escapeHtml(shippingLabel || '')}</p>
        <button class="button button--full-width button--primary cloudsoft-volume-offer__submit" type="submit" data-cloudsoft-submit>BUY NOW — $32.99</button>
        <div class="cloudsoft-volume-offer__trust" aria-label="Purchase assurances">
          <span>✓ ${shippingLabel}</span>
          <span>✓ Complimentary size support</span>
          <span>✓ Secure checkout</span>
        </div>
        <p class="cloudsoft-volume-offer__status" data-cloudsoft-status role="status" aria-live="polite" aria-atomic="true"></p>
      </form>`;
  }

  class CloudSoftVolumeOfferElement extends global.HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      const candidateNode = this.querySelector('[data-cloudsoft-candidates]');
      let rawCandidates;
      try {
        rawCandidates = JSON.parse(candidateNode?.textContent || '[]');
      } catch (error) {
        return;
      }

      const mediaNode = this.querySelector('[data-cloudsoft-variant-media]');
      let rawVariantMedia = [];
      try {
        rawVariantMedia = JSON.parse(mediaNode?.textContent || '[]');
      } catch (error) {
        rawVariantMedia = [];
      }

      const initialized = performInitialization({
        buildCustom: () => this.buildCustom(rawCandidates, rawVariantMedia),
        hideNative: () => this.hideNativeControls(),
      });
      if (!initialized) return;

      this.hidden = false;
      this.dataset.initialized = 'true';
    }

    buildCustom(rawCandidates, rawVariantMedia) {
      this.model = createOfferModel(rawCandidates);
      this.variantMedia = normalizeVariantMedia(rawVariantMedia);
      if (!this.model.candidates.length) throw new Error('No controlled QA candidates.');
      this.innerHTML = renderOfferMarkup(this.model.candidates, this.dataset.shippingLabel);
      this.form = this.querySelector('[data-cloudsoft-form]');
      this.submitButton = this.querySelector('[data-cloudsoft-submit]');
      this.statusNode = this.querySelector('[data-cloudsoft-status]');
      this.tierInputs = [...this.querySelectorAll('input[name="cloudsoft-pair-count"]')];
      this.pairRows = [...this.querySelectorAll('[data-cloudsoft-pair]')];
      if (!this.form || !this.submitButton || this.tierInputs.length !== 3 || this.pairRows.length !== 3) {
        throw new Error('Custom offer markup failed to initialize.');
      }
      if (this.tierInputs.filter((input) => input.checked).length !== 1 || !this.tierInputs[0].checked) {
        throw new Error('Exactly one default pair tier is required.');
      }

      this.submitOffer = createSubmitHandler({
        model: this.model,
        post: (items) => this.postItems(items),
        setBusy: (state) => this.setBusy(state),
        announce: (message) => this.announce(message),
        onSuccess: (state) => this.renderCart(state),
      });
      this.bindControls();
      this.syncPairRows();
      this.startCountdown();
      return this;
    }

    bindControls() {
      this.tierInputs.forEach((input) => input.addEventListener('change', () => {
        if (!input.checked || !this.model.setPairCount(Number(input.value))) return;
        this.syncPairRows();
      }));

      this.pairRows.forEach((row, index) => {
        const color = row.querySelector('[data-cloudsoft-color]');
        const size = row.querySelector('[data-cloudsoft-size]');
        color.addEventListener('change', () => {
          this.model.setSelection(index, 'color', color.value);
          this.model.setSelection(index, 'size', '');
          this.populateSizes(color.value, size);
          this.activateColorMedia(color.value);
          this.announce('');
        });
        size.addEventListener('change', () => {
          this.model.setSelection(index, 'size', size.value);
          this.announce('');
        });
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitOffer();
      });
    }

    activateColorMedia(color) {
      const mediaId = resolveMediaIdForColor(this.model.candidates, this.variantMedia, color);
      return activateGalleryMedia(this, mediaId);
    }

    populateSizes(color, select) {
      select.innerHTML = '<option value="">Choose size</option>'
        + renderSizeOptions(this.model.candidates, color);
      select.disabled = !color;
      select.value = '';
    }

    syncPairRows() {
      this.pairRows.forEach((row, index) => {
        const active = index < this.model.pairCount;
        row.hidden = !active;
        row.querySelectorAll('select').forEach((select) => {
          if (!active) select.disabled = true;
          else if (select.matches('[data-cloudsoft-color]')) select.disabled = false;
          else select.disabled = !this.model.selections[index].color;
        });
      });
      if (this.submitButton) {
        this.submitButton.textContent = `BUY NOW — ${TIER_TOTALS[this.model.pairCount]}`;
        this.submitButton.setAttribute('aria-label', `Buy ${this.model.pairCount} pair${this.model.pairCount === 1 ? '' : 's'} for ${TIER_TOTALS[this.model.pairCount]}`);
      }
    }

    startCountdown() {
      const countdown = this.querySelector('[data-cloudsoft-countdown]');
      if (!countdown) return;
      const render = () => {
        const remaining = Math.max(0, PROMO_END_AT - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const values = {
          days: Math.floor(totalSeconds / 86400),
          hours: Math.floor((totalSeconds % 86400) / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60,
        };
        Object.entries(values).forEach(([unit, value]) => {
          const node = countdown.querySelector(`[data-cloudsoft-${unit}]`);
          if (node) node.textContent = String(value).padStart(2, '0');
        });
        if (remaining === 0 && this.countdownTimer) {
          global.clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      };
      render();
      this.countdownTimer = global.setInterval(render, 1000);
    }

    disconnectedCallback() {
      if (this.countdownTimer) global.clearInterval(this.countdownTimer);
    }

    async postItems(items) {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const payload = { items };
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = global.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }

      const response = await global.fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const state = await response.json();
      if (!response.ok || state?.status) {
        throw new Error(state?.description || state?.message || 'Unable to add your selection to cart.');
      }
      this.cart = cart;
      return state;
    }

    renderCart(state) {
      if (this.cart?.renderContents) {
        this.cart.renderContents(state);
        return;
      }
      global.location.assign(global.routes?.cart_url || '/cart');
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.toggleAttribute('aria-busy', isBusy);
    }

    announce(message) {
      this.statusNode.textContent = message;
    }

    hideNativeControls() {
      hideNativeControlsForOffer(this, global.document);
    }
  }

  function hideNativeControlsForOffer(root, documentRoot) {
    const productInfo = root.closest('product-info');
    if (!productInfo) return 0;
    const controls = [
      ...productInfo.querySelectorAll('variant-selects, variant-radios, .product-form__quantity, product-form'),
      ...(documentRoot?.querySelectorAll('[data-novenu-sticky-buy]') || []),
    ];
    if (!controls.some((control) => control.matches('product-form'))) {
      throw new Error('Native product form fallback not found.');
    }
    controls.forEach((control) => {
      control.hidden = true;
      control.setAttribute('aria-hidden', 'true');
      control.dataset.cloudsoftNativeHidden = 'true';
      control.querySelectorAll('button, input, select').forEach((field) => {
        field.disabled = true;
      });
    });
    return controls.length;
  }

  const api = {
    SIZE_ORDER,
    normalizeCandidates,
    normalizeVariantMedia,
    resolveMediaIdForColor,
    activateGalleryMedia,
    groupVariantIds,
    createOfferModel,
    createSubmitHandler,
    performInitialization,
    hideNativeControlsForOffer,
    renderOfferMarkup,
    renderSizeOptions,
  };
  global.CloudSoftVolumeOffer = api;
  if (global.customElements && !global.customElements.get('cloudsoft-volume-offer')) {
    global.customElements.define('cloudsoft-volume-offer', CloudSoftVolumeOfferElement);
  }
}(globalThis));
